import React, { createContext, useContext, useState, useCallback } from 'react';
import pushReprocess, { PushReprocessResponse } from '../services/pushReprocess';

interface IPushReprocessContext {
  cnjIds: string[];
  setCnjIds: (data: string[]) => void;
  results: PushReprocessResponse | null;
  setResults: (data: PushReprocessResponse | null) => void;
  reprocessPush: (cnjIds: string[]) => Promise<PushReprocessResponse>;
  loading: boolean;
  error: string | null;
}

export const PushReprocessContext = createContext<IPushReprocessContext>({
  cnjIds: [],
  setCnjIds: () => {},
  results: null,
  setResults: () => {},
  reprocessPush: async () => ({ success: false, data: [] }),
  loading: false,
  error: null,
});

const PushReprocessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cnjIds, setCnjIdsState] = useState<string[]>([]);
  const [results, setResults] = useState<PushReprocessResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Wrapper para setCnjIds que limpa os resultados anteriores quando novos IDs são definidos
  const setCnjIds = useCallback((ids: string[]) => {
    // Limpa resultados anteriores quando novos IDs são definidos
    setResults(null);
    setError(null);
    setCnjIdsState(ids);
  }, []);

  const reprocessPush = useCallback(async (ids: string[]): Promise<PushReprocessResponse> => {
    try {
      setLoading(true);
      setError(null);
      const response = await pushReprocess.reprocess({ cnjIds: ids });
      setResults(response);
      return response;
    } catch (err: any) {
      console.error('Erro ao reprocessar Push:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Erro ao reprocessar Push';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <PushReprocessContext.Provider
      value={{
        cnjIds,
        setCnjIds,
        results,
        setResults,
        reprocessPush,
        loading,
        error,
      }}
    >
      {children}
    </PushReprocessContext.Provider>
  );
};

const usePushReprocess = (): IPushReprocessContext => {
  const context = useContext(PushReprocessContext);
  if (!context) {
    throw new Error('usePushReprocess must be used within a PushReprocessProvider');
  }
  return context;
};

export { PushReprocessProvider, usePushReprocess };

